import { useEffect, useState } from "preact/hooks";
import { formatCurrencyNumber } from "@/util/common.js";
import "./gifts-that-give.setup.scss";
import { tiltify } from "@playlive/tiltify-lib";
import { useQuery } from "@tanstack/react-query";
import LoadingSpinner from "@/components/shared/loading-spinner.jsx";
import useGiftsThatGive from "@/hooks/useGiftsThatGive.js";
import useTiltifyConfig from "@/hooks/useTiltifyConfig.js";

export default function GiftsThatGiveOverlaySetup() {
  const [giftsThatGiveList, setGiftsThatGiveList] = useState([]);
  const [giftsThatGiveTotal, setGiftsThatGiveTotal] = useState(0);
  const tiltifyConfig = useTiltifyConfig();
  const { tiltifyUserID, tiltifyCampaignID } = tiltifyConfig;

  let tiltifyCampaignGoal = tiltifyConfig.tiltifyCampaignGoal;
  const { data: campaigns, isPending: campaignsLoading } = useQuery({
    queryKey: ["tiltify", tiltifyUserID, "getCampaigns"],
    queryFn: async () => {
      if (!tiltifyUserID) {
        return [];
      }
      tiltify.UseProxy();
      try {
        return (await tiltify.GetUserCampaigns(tiltifyUserID)).filter(
          (c) => c.status === "published",
        );
      } catch (err) {
        throw new Error(err.message);
      }
    },
    placeholderData: [],
  });

  if (
    (!tiltifyCampaignGoal || tiltifyCampaignGoal === "undefined" || tiltifyCampaignGoal === "0") &&
    !campaignsLoading &&
    tiltifyCampaignID
  ) {
    const campaign = campaigns.find((campaign) => campaign.id === tiltifyCampaignID);

    if (campaign) {
      tiltifyCampaignGoal = campaign.goal.value;
    }
  }

  useEffect(() => {
    setGiftsThatGiveTotal(giftsThatGiveList.reduce((n, { amount }) => n + amount, 0));
  }, [giftsThatGiveList]);

  const amount = useGiftsThatGive({
    goal: tiltifyCampaignGoal,
  });

  useEffect(() => {
    if (!amount) {
      return;
    }

    const url = `${import.meta.env.VITE_OVERLAY_SERVICE_URI}/gifts-that-give-milestones`;
    const body = JSON.stringify({ goal: amount });

    fetch(url, {
      method: "POST",
      body,
      mode: "cors",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((r) => r.json())
      .then((data) => {
        setGiftsThatGiveList(
          data.map((giftsThatGive) => {
            return {
              id: giftsThatGive.id,
              amount: Number(giftsThatGive.value_rounded),
              provide: giftsThatGive.provide_cover,
              icon: giftsThatGive.icon,
              description: giftsThatGive.description,
            };
          }),
        );
      });
  }, [amount]);

  return (
    <div
      className={`gifts_that_give_overlay_setup card card-bordered bg-base-100 min-h-40 mt-4 mb-4 transition-opacity ${campaignsLoading || tiltifyCampaignGoal === null || tiltifyCampaignGoal === "undefined" ? "opacity-0" : "opacity-100"}`}
    >
      <div className="card-body">
        <p className="mt-2 mb-2">
          The goal for your selected campaign is ${formatCurrencyNumber(tiltifyCampaignGoal)}
        </p>
        <p className="text-secondary mb-2">Your Gifts that Give amounts will be:</p>
        <ul className="min-h-80">
          <LoadingSpinner show={giftsThatGiveList.length === 0} />
          {giftsThatGiveList.map((gifts) => (
            <li className="mb-4 flex items-center" key={gifts.id}>
              <div className={`icon w-12 h-12 grey ${gifts.icon}`} />
              <div className="flex flex-col justify-center ml-6 flex-grow">
                <span className="text-xl">${formatCurrencyNumber(gifts.amount)}</span>
                <span>{gifts.description}</span>
              </div>
            </li>
          ))}
        </ul>
        <p className="text-sm">
          Total amount raised to complete all Gifts that Give amounts:{" "}
          <strong>${formatCurrencyNumber(giftsThatGiveTotal)}</strong>
        </p>
      </div>
    </div>
  );
}
